import { useQuery } from "@apollo/client";
import { useCallback, useRef } from "react";
import { useHistory } from "react-router";

import ConsultReportForm from "@components/forms/ConsultReportForm";
import HandleQuery from "@components/HandleQuery";
import ExpertJourneyPage from "@components/pageTypes/ExpertJourneyPage";
import useSubscribeToConsult from "@hooks/subscriptions/useSubscribeToConsult";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useMyExpert from "@hooks/useMyExpert";
import {
  GetConsultDocument,
  GetConsultQuery,
  GetConsultQueryVariables,
  GetJourneyWithClientRolesDocument,
  GetJourneyWithClientRolesQuery,
  GetJourneyWithClientRolesQueryVariables
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  consultId: string;
  journeyId: string;
};

const ExpertJourneyConsultReportEditPage = ({ consultId, journeyId }: Props) => {
  const history = useHistory();
  const myExpert = useMyExpert();
  const contentRef = useRef<HTMLIonContentElement>(null);

  const getConsultQuery = useQuery<GetConsultQuery, GetConsultQueryVariables>(GetConsultDocument, {
    variables: { id: consultId }
  });

  useSubscribeToConsult(consultId, () => {
    getConsultQuery.refetch();
  });

  const getJourneyQuery = useQuery<GetJourneyWithClientRolesQuery, GetJourneyWithClientRolesQueryVariables>(
    GetJourneyWithClientRolesDocument,
    {
      variables: { id: journeyId }
    }
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  const handleSuccess = useCallback(() => {
    history.push(journeyRoute({ journeyId, linkAfterJourney: `/consults/${consultId}/consult-report`, myExpert }));
  }, [history, journeyId, consultId, myExpert]);

  const handleError = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollToTop(2000);
    }
  }, [contentRef]);

  return (
    <ExpertJourneyPage
      contentRef={contentRef}
      helmetKey="pageTitles.expertJourney.consultReport.edit"
      journeyId={journeyId}
    >
      <HandleQuery query={getJourneyQuery}>
        {journeyData => (
          <HandleQuery query={getConsultQuery}>
            {data => (
              <div className="contentSpacing">
                {data.consult.consultReport && (
                  <ConsultReportForm
                    autofocus
                    consult={data.consult}
                    journey={journeyData.journey}
                    onCancel={handleSuccess}
                    onError={handleError}
                    onSuccess={handleSuccess}
                  />
                )}
              </div>
            )}
          </HandleQuery>
        )}
      </HandleQuery>
    </ExpertJourneyPage>
  );
};

export default ExpertJourneyConsultReportEditPage;
